import React, {
  ForwardedRef,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { MATCH_RULE_TYPE_STR, MatchRuleEntity } from 'src/store/matchRuleApi';
import { selectSortedMatchRules } from 'src/store/matchRuleSlice';
import { Panel } from 'src/ui/Panel';
import { Badge } from 'src/ui/Badge';

export interface ContentProps {
  setSelectedMatchRule: (matchRule: MatchRuleEntity) => void;
}

export interface ContentRef {
  scrollTo: (id: number) => void;
}

const Content = forwardRef(
  ({ setSelectedMatchRule }: ContentProps, ref: ForwardedRef<ContentRef>) => {
    const orderedMatchRules = useSelector(selectSortedMatchRules);
    const contentRef = useRef<Record<number, HTMLDivElement | null>>({});
    const enabledRules = orderedMatchRules.filter((r) => !r.disabled);
    const totalWeight = enabledRules.reduce(
      (s, r) => s + (r.weightMale + r.weightFemale) / 2,
      0
    );

    useImperativeHandle(ref, () => ({
      scrollTo: (id: number) => {
        if (contentRef.current[id]) {
          contentRef.current[id]?.scrollIntoView();
        }
      },
    }));

    return (
      <Panel
        className="grow"
        title="Content"
        icon="list"
        scroll
        headerRight={
          <Badge color={Math.round(totalWeight) === 100 ? 'green' : 'amber'}>
            {enabledRules.length} rules
          </Badge>
        }
      >
        <div className="col gap-3">
          {orderedMatchRules.map((matchRule, i) => (
            <div
              key={matchRule.id}
              ref={(el) => {
                contentRef.current[matchRule.id] = el;
              }}
              id={String(matchRule.id)}
              role="button"
              tabIndex={0}
              style={{
                opacity: matchRule.disabled ? 0.55 : 1,
                cursor: 'pointer',
              }}
              onClick={() => setSelectedMatchRule(matchRule)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ')
                  setSelectedMatchRule(matchRule);
              }}
            >
              <div className="row gap-2" style={{ marginBottom: 6 }}>
                <span
                  className="mono text-muted"
                  style={{ fontSize: 'var(--fs-2xs)' }}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>
                  {matchRule.name}
                </span>
                <Badge color="gray" size="sm">
                  {MATCH_RULE_TYPE_STR[matchRule.type]}
                </Badge>
              </div>
              <div
                style={{
                  fontSize: 'var(--fs-xs)',
                  color: 'var(--text-secondary)',
                  background: 'var(--bg-muted)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '6px 10px',
                  lineHeight: 1.5,
                }}
              >
                weight ♂ {matchRule.weightMale}% / ♀ {matchRule.weightFemale}%
                {matchRule.prompt && (
                  <div
                    className="mono"
                    style={{
                      marginTop: 4,
                      fontSize: 'var(--fs-2xs)',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {matchRule.prompt.slice(0, 120)}
                    {matchRule.prompt.length > 120 ? '…' : ''}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Panel>
    );
  }
);

export default Content;
